import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Refrigerator, Loader2, Flame, Beef } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { supabase } from '../../../lib/supabaseClient'
import { imageToBase64 } from '../../../lib/imageToBase64'

// Recipe-from-fridge — snap the fridge, Gemini vision identifies
// what's there and suggests 3 meals matched to macro targets, the
// active phase and recent meal-log patterns. Photo is ephemeral —
// never uploaded to storage, only the suggestion comes back.

export default function FridgeRecipes() {
  const { t } = useTranslation()
  const fileRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || busy) return
    setBusy(true)
    setResult(null)
    try {
      const { base64, mime } = await imageToBase64(file)
      const { data, error } = await supabase.functions.invoke('fridge-recipes', {
        body: { image_base64: base64, mime },
      })
      setResult(error || !data?.recipes ? { failed: true } : data)
    } catch {
      setResult({ failed: true })
    }
    setBusy(false)
  }

  return (
    <Card>
      <CardContent className="p-3 sm:p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="flex-1 min-w-0 text-sm font-semibold flex items-center gap-1.5 truncate" dir="auto">
            <Refrigerator className="w-4 h-4 text-app-primary-500" />
            {t('fridge.title')}
          </h2>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-app-primary-500/10 text-app-primary-500 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            dir="auto"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Refrigerator className="w-4 h-4" />}
            {busy ? t('fridge.thinking') : t('fridge.snap')}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleFile} className="hidden" />

        {!result && !busy && (
          <p className="text-xs text-muted-foreground" dir="auto">{t('fridge.hint')}</p>
        )}

        {result?.failed && (
          <p className="text-xs text-red-500" dir="auto">{t('fridge.failed')}</p>
        )}

        {result?.ingredients?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {result.ingredients.map((ing, i) => (
              <span key={i} className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground text-[10px]" dir="auto">{ing}</span>
            ))}
          </div>
        )}

        {result?.recipes?.map((r, i) => (
          <div key={i} className="rounded-md border border-border p-3 space-y-1.5" dir="auto">
            <div className="flex items-center gap-2">
              <p className="flex-1 min-w-0 text-sm font-semibold text-foreground truncate">{r.name}</p>
              <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground flex-shrink-0" dir="ltr">
                <Flame className="w-3 h-3" />{r.kcal}
              </span>
              <span className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground flex-shrink-0" dir="ltr">
                <Beef className="w-3 h-3" />{r.protein_g}g
              </span>
            </div>
            {r.why && <p className="text-xs text-app-primary-500">{r.why}</p>}
            {Array.isArray(r.steps) && (
              <ol className="text-xs text-muted-foreground space-y-0.5 ps-4 list-decimal">
                {r.steps.map((s, j) => <li key={j}>{s}</li>)}
              </ol>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
